import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { POST_URL } from '@utils/api/endpoints';
import { URL_EXTENSION } from '@utils/constants/app-constants';
import { CustomerBo } from '@utils/types/back-office/customers';
import { BaseResponse } from '@utils/types/shared/base';
import {
  OrgSwitcher as SharedOrgSwitcher,
  UseOrgsDropdownResult
} from '@components/shared/org-switcher/org-switcher';

// SYSOP-scoped org list: hits /admin/customer, supports search by id or name.
function useSysopOrgsDropdown(
  q: string,
  enabled: boolean
): UseOrgsDropdownResult {
  const query = useQuery<BaseResponse<CustomerBo[]>>(
    ['sysop-orgs-dropdown', q],
    async () => {
      const params = new URLSearchParams({ offset: '0', limit: '50' });
      if (q) {
        const isId = /^\d+$/.test(q.trim());
        params.append('q', isId ? `id:eq:${q.trim()}` : `name:ilike:${q}`);
      }
      const { data } = await Axios.get<BaseResponse<CustomerBo[]>>(
        `${URL_EXTENSION}/admin/customer?${params.toString()}`
      );
      return data;
    },
    { staleTime: 30_000, keepPreviousData: true, enabled }
  );

  const options = useMemo(
    () =>
      (query.data?.result ?? []).map(org => ({
        id: org.id,
        label: org.name,
        value: org.id,
        email: org.email,
        isActive: org.is_active !== false
      })),
    [query.data]
  );

  return {
    options,
    isLoading: query.isLoading,
    isFetching: query.isFetching
  };
}

// Thin SYSOP wrapper around the shared switcher. Kept as a named export to
// preserve the existing import path (`@components/back-office/org-switcher`).
export function OrgSwitcher() {
  return (
    <SharedOrgSwitcher
      homeOrgKeyPrefix="sysop"
      switchEndpoint={POST_URL().switchOrganization}
      useOptions={useSysopOrgsDropdown}
      homeRedirectTo="/back-office/customers"
      dropdownPlacement="up"
    />
  );
}
